/**
 * How big a picture is, WITHOUT decoding it.
 *
 * @Codex, P1 on #184:
 *
 * > *"For a highly compressed but very large image, the 64 MiB file-size check
 * > does not bound decoded pixels: this call materializes the full-resolution
 * > bitmap before `fitWithin()` reduces it to 320 px. A small solid-color PNG
 * > with dimensions such as 10000×10000 can therefore require hundreds of
 * > megabytes at this line and freeze or terminate the tab even though it
 * > easily passes ingestion's byte ceiling."*
 *
 * Correct, and the arithmetic is worse than "hundreds": 10000×10000 at four
 * bytes a pixel is **400 MB**, from a PNG that compresses to a few kilobytes.
 * `MAX_BYTES` is 64 MiB and was chosen from a measured table of what STORING
 * costs; it says nothing about what decoding costs, and the two are unrelated
 * for exactly the files that matter here.
 *
 * ## Why a parser rather than a decoder option
 *
 * `createImageBitmap` takes `resizeWidth`/`resizeHeight`, and passing them is
 * worth doing — a decoder that can downsample while decoding will. But the
 * specification only permits that; it does not require it, and implementations
 * commonly decode in full and then resize. So a resize option is a hint and
 * cannot be the bound.
 *
 * It also cannot be *computed* without the size: preserving aspect ratio means
 * knowing the ratio, and the only way to learn it from the platform is to
 * decode. Which is the thing being bounded.
 *
 * Every format the door accepts states its dimensions in a header, in the first
 * few dozen bytes, and reading them is arithmetic on a `Uint8Array`. So this
 * runs in Node, is unit-testable without a browser, and cannot itself be the
 * bomb — it never allocates anything proportional to the picture.
 */

/** Intrinsic pixels, or `undefined` when the header could not be read. */
export interface PictureSize {
  readonly width: number;
  readonly height: number;
}

/**
 * The most pixels a derivative may decode from.
 *
 * 64 megapixels, which is **256 MB** at four bytes a pixel.
 *
 * Chosen against the cameras people own rather than measured against where a
 * browser dies, and that is a judgement rather than a number — say so, because
 * `MAX_BYTES` one file over IS measured and sits beside a table, and a reader
 * would reasonably assume this one is too. What sets it is the largest ordinary
 * still: a 50 MP full-frame frame at 8192×6144 is 50.3 MP, and a budget that
 * refused it would be a refusal wearing a limit. A 102 MP medium-format frame
 * is over, and so is every decompression bomb worth the name.
 *
 * 256 MB of transient allocation is not free and is not being pretended to be
 * — on a phone it is a real risk. It is the smaller risk: the alternative is
 * unbounded, and 10000×10000 from a few kilobytes of PNG is 400 MB.
 *
 * A file above this is refused its preview and **still ingests** — clause 6 of
 * task 0118: the missing derivative is visible, and it costs the picture rather
 * than the file.
 */
export const MAX_DECODED_PIXELS = 64_000_000;

const ascii = (bytes: Uint8Array, at: number, text: string): boolean => {
  for (let i = 0; i < text.length; i += 1) if (bytes[at + i] !== text.charCodeAt(i)) return false;
  return true;
};

const be32 = (b: Uint8Array, at: number) =>
  ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
const be16 = (b: Uint8Array, at: number) => (b[at]! << 8) | b[at + 1]!;
const le16 = (b: Uint8Array, at: number) => b[at]! | (b[at + 1]! << 8);
const le24 = (b: Uint8Array, at: number) => b[at]! | (b[at + 1]! << 8) | (b[at + 2]! << 16);
/**
 * Arithmetic, not bitwise, and that is the whole reason it is its own function.
 *
 * A RIFF chunk length is unsigned 32-bit. `x | (y << 24)` coerces to int32, so
 * a length with its top bit set comes back NEGATIVE — the first draft of the
 * WebP walk had exactly that and would have refused a large chunk for the wrong
 * reason, which is a guard being red about the wrong question.
 */
const le32 = (b: Uint8Array, at: number) =>
  b[at]! + b[at + 1]! * 0x100 + b[at + 2]! * 0x10000 + b[at + 3]! * 0x1000000;

/**
 * PNG: an 8-byte signature, then the IHDR chunk, whose first eight payload
 * bytes are width and height. Fixed offsets — IHDR is required to be first.
 *
 * ## All eight signature bytes, and the IHDR length
 *
 * @Codex, **P1** on #184: *"For a crafted accepted file, checking only bytes
 * 1–3 for `PNG` lets another decodable format masquerade as a small PNG: for
 * example, an AVIF can use `0x00504e47` as its first box size, `ftyp` at byte
 * 4, `IHDR` as the minor-version bytes at 12, and small integers at 16/20
 * while its real `ispe` declares a much larger image. Because the declared PNG
 * parser succeeds, `pictureSize()` never runs the AVIF parser, and
 * `createImageBitmap()` can sniff and decode the real image past
 * `MAX_DECODED_PIXELS`."*
 *
 * The whole exploit is that this function returned a size it was confident
 * about. `pictureSize` tries the declared parser first and only falls through
 * to the others when it returns `undefined` — which is right, and it makes a
 * loose signature check into a way to switch the real parser off. A parser that
 * refuses is safe; a parser that answers wrongly is the hole.
 *
 * So: the full `89 50 4E 47 0D 0A 1A 0A`, and the IHDR chunk length, which a
 * real PNG requires to be exactly 13. Both are cheap and both are structural —
 * the four bytes at offset 8 are a length in a PNG and a brand in an ISOBMFF
 * file, so requiring 13 there is the same check as "this is not an AVIF".
 *
 * `0x0d0a1a0a` is the reason the signature is eight bytes rather than four in
 * the format itself: it catches CR/LF translation and 8-bit-stripping
 * transports. Checking three of them was strictly weaker than what the format
 * already provides.
 */
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const IHDR_LENGTH = 13;

/**
 * ...and every APNG frame rectangle that claims to exceed the canvas.
 *
 * @Codex, P1 on `78f39a7`: *"When a PNG has a small `IHDR` canvas but an
 * animated `fcTL` chunk declares a much larger frame rectangle, this returns
 * only the canvas dimensions, so the file passes the 64-megapixel check before
 * `createImageBitmap()` processes the oversized frame. Walk the bounded PNG
 * chunk structure and include every APNG frame extent, as the GIF and animated
 * WebP parsers do."*
 *
 * The THIRD time in this file, and the same defect each time: a parser
 * answering *"how big does the author say the canvas is"* when the ceiling
 * asked *"how much might the decoder allocate"*. The GIF version above has the
 * argument in full; nothing about it is different here except the container.
 *
 * `fcTL` is 26 bytes of pure header — sequence, width, height, x and y offsets,
 * delay, dispose and blend — so reading it is reading structure. **The walk
 * steps over `IDAT` and `fdAT` by their declared lengths and never looks
 * inside one**, which is `boxes()`'s rule and the reason this file has it: a
 * byte scan for a four-character code finds that code inside compressed media.
 *
 * Bounded like the others, and exhausting the budget REFUSES rather than
 * returning what it found — the chunks it did not reach are exactly the ones it
 * was asked about.
 */
function png(b: Uint8Array): PictureSize | undefined {
  if (b.length < 24) return undefined;
  for (let at = 0; at < PNG_SIGNATURE.length; at += 1) {
    if (b[at] !== PNG_SIGNATURE[at]) return undefined;
  }
  if (be32(b, 8) !== IHDR_LENGTH || !ascii(b, 12, 'IHDR')) return undefined;
  let width = be32(b, 16);
  let height = be32(b, 20);

  /** Every chunk is `length(4) type(4) data(length) crc(4)`. */
  let at = 8;
  let steps = HEADER_SCAN_STEPS;
  while (at + 8 <= b.length) {
    if (steps <= 0) return undefined;                      // exhausted, so refuse
    steps -= 1;
    const length = be32(b, at);
    // A length that runs past the buffer is malformed, and a walk that cannot
    // continue has not measured what comes after it.
    if (length > b.length - at - 8) return undefined;
    // 24 bytes of data carry sequence, width, height and both offsets — the
    // whole of what this needs. A shorter `fcTL` is malformed and is stepped
    // over rather than read, because the length guard above has already proved
    // only that the chunk fits, not that it is complete.
    if (ascii(b, at + 4, 'fcTL') && length >= 24) {
      // The extent a decoder may have to allocate for: the placement plus the
      // size, exactly as the GIF descriptor above. `fcTL`'s data opens with a
      // 4-byte sequence number, so from the chunk start it is
      // `+12 width, +16 height, +20 x, +24 y`.
      width = Math.max(width, be32(b, at + 20) + be32(b, at + 12));
      height = Math.max(height, be32(b, at + 24) + be32(b, at + 16));
    }
    if (ascii(b, at + 4, 'IEND')) break;
    at += 12 + length;                                     // length + type + crc
  }
  return { width, height };
}

/**
 * GIF: the logical screen, and every frame rectangle that claims to exceed it.
 *
 * @Codex, P1 on #184: *"When a GIF declares a small logical screen here but
 * contains an image descriptor with a much larger frame rectangle,
 * `pictureSize()` reports only the screen dimensions while
 * `createImageBitmap()` may allocate from the frame dimensions before clipping
 * or rejecting the malformed rectangle. A tolerated file can therefore pass the
 * 64-megapixel check yet request a frame approaching 65,535×65,535 pixels."*
 *
 * Right, and it is the same defect as the PNG signature one round earlier: this
 * function's job is to tell the ceiling how much the decoder might allocate,
 * and it was answering a different question — how big the author says the
 * canvas is. The comment it replaces said *"the frames may be smaller"*, which
 * is true of every well-formed GIF and is exactly the assumption a hostile file
 * does not honour. 65,535² is 4.29 gigapixels against a 64-megapixel ceiling.
 *
 * So the ceiling is given the largest extent any descriptor claims, and the
 * existing refusal does the rest — a new refusal here would be a second
 * opinion about a limit that already exists.
 *
 * **Structure only, never the payload.** The walk steps over LZW data by the
 * sub-block lengths the format already carries; it never looks inside one.
 * That is the rule `boxes()` follows for AVIF, and the reason `image-size.ts`
 * has it: a byte scan for a marker finds that marker inside compressed media.
 * Bounded by the same step count as the JPEG walk, for the same reason — a
 * valid file takes one step per block, a malformed one exhausts the budget.
 */
function gif(b: Uint8Array): PictureSize | undefined {
  // The FULL signature. `GIF8` alone admits `GIF8` plus any two bytes, which is
  // not a version this or any decoder knows.
  if (b.length < 13 || !(ascii(b, 0, 'GIF87a') || ascii(b, 0, 'GIF89a'))) return undefined;
  let width = le16(b, 6);
  let height = le16(b, 8);

  const packed = b[10]!;
  // Global colour table: 3 bytes per entry, 2^(n+1) entries, when the flag is set.
  let at = 13 + ((packed & 0x80) !== 0 ? 3 * (1 << ((packed & 0x07) + 1)) : 0);

  /** Step over one chain of length-prefixed sub-blocks. Returns the end, or -1. */
  const subBlocks = (from: number, steps: { left: number }): number => {
    let cursor = from;
    while (cursor < b.length && steps.left > 0) {
      steps.left -= 1;
      const size = b[cursor]!;
      if (size === 0) return cursor + 1;
      cursor += 1 + size;
    }
    return -1;
  };

  /**
   * Exhausting the budget is a REFUSAL, not a result.
   *
   * @Codex, P1 on #184, on the fix directly above: *"exhausting `steps.left`
   * falls through to this successful return. A valid GIF can place roughly
   * 1,366 short comment extensions before an oversized image descriptor; the
   * scan then returns only the small logical-screen dimensions, allowing
   * `createImageBitmap()` to decode the uninspected frame past the
   * 64-megapixel ceiling."*
   *
   * Right, and it is the bound I added to make the walk safe handing back the
   * bypass the walk was written to close — the same shape as the depth bound in
   * `pool.ts` that turned a hostile input into the way past a check. A bound on
   * a PARSER may stop and report what it found; a bound on something feeding a
   * SAFETY CEILING must refuse, because the frames it did not reach are exactly
   * the ones it was asked about.
   *
   * `undefined` is what the AVIF parser already returns when its own walk runs
   * out, and `pictureSize()`'s caller treats an unknown size as undecodable
   * rather than as small.
   */
  let exhausted = false;

  const steps = { left: HEADER_SCAN_STEPS };
  while (at < b.length && steps.left > 0) {
    steps.left -= 1;
    const block = b[at]!;
    if (block === 0x3b) break;                                  // trailer
    if (block === 0x21) {                                        // extension
      // label at +1, then its sub-blocks
      const end = subBlocks(at + 2, steps);
      if (end < 0) { exhausted = true; break; }
      at = end;
      continue;
    }
    if (block === 0x2c) {                                        // image descriptor
      if (at + 10 > b.length) break;
      const left = le16(b, at + 1);
      const top = le16(b, at + 3);
      const frameWidth = le16(b, at + 5);
      const frameHeight = le16(b, at + 7);
      // The extent a decoder may have to allocate for, which is the placement
      // plus the size — a 1x1 screen with a frame at (60000, 60000) is not a
      // small picture just because its own width is small.
      width = Math.max(width, left + frameWidth);
      height = Math.max(height, top + frameHeight);

      const local = b[at + 9]!;
      let after = at + 10 + ((local & 0x80) !== 0 ? 3 * (1 << ((local & 0x07) + 1)) : 0);
      after += 1;                                                // LZW minimum code size
      const end = subBlocks(after, steps);
      if (end < 0) { exhausted = true; break; }
      at = end;
      continue;
    }
    break;                                                       // not a block we know
  }
  // Ran out mid-walk rather than reaching a trailer: there may be descriptors
  // beyond here and none of them was measured.
  if (steps.left <= 0) exhausted = true;

  return exhausted ? undefined : { width, height };
}

/**
 * JPEG: walk the marker segments to the start-of-frame.
 *
 * There is no fixed offset — a file may carry EXIF, ICC and any number of
 * comment segments first, and a phone photograph usually carries all three. The
 * walk is bounded by the file and touches only segment headers.
 */
/**
 * How many steps a header scan may take.
 *
 * @Codex, P2 on #184, twice. First: *"For a malformed or truncated JPEG near
 * the accepted 64 MiB ceiling, `jpeg()` can advance byte by byte through the
 * entire file, so this performs roughly 128 million synchronous iterations on
 * the main thread."* Then, on the fix: *"For a valid JPEG containing more than
 * 8 MiB of APP or COM metadata before its start-of-frame marker, this absolute
 * byte cutoff stops the scan and makes `pictureSize()` return `undefined` …
 * even though the browser can decode the accepted JPEG. Since valid segments
 * already carry lengths and are skipped in one iteration, bound the number of
 * markers/iterations instead."*
 *
 * Both right, and the second is the better bound because it separates the two
 * cases the first one conflated. A **valid** JPEG reaches its frame header in
 * one step per segment however large those segments are — a 40 MiB raw-plus-
 * thumbnail EXIF is a handful of steps. A **malformed** one has no lengths to
 * follow and advances a byte at a time, so it spends this budget almost
 * immediately.
 *
 * A byte offset cannot tell those apart; a step count is exactly the thing that
 * differs. 4,096 is far more segments than any real file carries and far fewer
 * steps than a byte walk needs to do damage.
 */
const HEADER_SCAN_STEPS = 4096;

/**
 * How many single BYTES the walk may step over, counted apart from segments.
 *
 * @Codex, P2 on #184, and it is the opposite direction from every other finding
 * on this file: *"When an accepted JPEG contains more than 4,096 legal `0xff`
 * fill bytes before its start-of-frame marker, this increments `steps` for
 * every fill byte and reaches the cutoff even though it merely advances through
 * padding. `pictureSize()` then returns `undefined`, so ingestion stores the
 * source but refuses a preview for a JPEG the browser can decode."*
 *
 * The budget was written to bound a MALFORMED scan and it was charging a
 * well-formed one. JPEG permits any number of `0xff` bytes before a marker, so
 * padding is not evidence of anything — and a step count that cannot tell
 * padding from a segment is the same conflation the step count itself was
 * introduced to fix, one level down. That round replaced a byte cutoff with a
 * step cutoff because *"a valid JPEG reaches its frame header in one step per
 * segment however large those segments are"*; this one separates the two things
 * a step could mean.
 *
 * 65,536 is far more fill than any encoder emits and four orders of magnitude
 * below the ~128 million byte-at-a-time iterations the original bound existed
 * to prevent.
 */
const HEADER_SCAN_BYTES = 65536;

function jpeg(b: Uint8Array): PictureSize | undefined {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return undefined;
  let at = 2;
  // Segments and single-byte advances are bounded SEPARATELY: one is what a
  // valid file spends, the other is what a malformed one spends.
  let segments = 0;
  let bytes = 0;
  while (at + 9 < b.length) {
    if (b[at] !== 0xff) {                             // not at a marker at all
      bytes += 1;
      if (bytes > HEADER_SCAN_BYTES) return undefined;
      at += 1;
      continue;
    }
    const marker = b[at + 1]!;
    if (marker === 0xff) {                            // legal fill before a marker
      bytes += 1;
      if (bytes > HEADER_SCAN_BYTES) return undefined;
      at += 1;
      continue;
    }
    segments += 1;
    if (segments > HEADER_SCAN_STEPS) return undefined;
    // SOF0..SOF15, excluding DHT (c4), DAC (cc) and the RSTn range (d0-d7).
    // Every one of them carries the frame's dimensions in the same place.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8
      && marker !== 0xcc) {
      return { height: be16(b, at + 5), width: be16(b, at + 7) };
    }
    /**
     * The STANDALONE markers, which carry no length field at all.
     *
     * `0x01` is TEM, and it was missing. @Codex, P2 on #184: *"this code treats
     * the two bytes following TEM as a segment length even though TEM has no
     * length field. It consequently jumps past the real frame header or exits
     * the walk, so `pictureSize()` returns `undefined` and ingestion refuses to
     * create a preview for a JPEG the browser can decode."*
     *
     * The others are SOI (`d8`), the eight restart markers (`d0`-`d7`) and EOI
     * (`d9`). Reading a length that is not there is worse than not recognising
     * the marker: it advances by an arbitrary number taken from image data.
     */
    if (marker === 0x01 || marker === 0xd8
      || (marker >= 0xd0 && marker <= 0xd9)) { at += 2; continue; }
    if (marker === 0xda) return undefined;            // scan data: no frame header found
    const length = be16(b, at + 2);
    if (length < 2) return undefined;
    at += 2 + length;
  }
  return undefined;
}

/**
 * WebP: three container shapes, and they disagree about where the size lives.
 *
 * `VP8X` is the extended form an animation or an alpha channel uses and it
 * states the CANVAS, which is what a decoder produces. `VP8 ` is lossy and
 * `VP8L` lossless, each with the dimensions packed into their own bitstream
 * header. Reading only the first is how a thumbnailer misses two thirds of the
 * WebPs it will be given.
 */
/**
 * The animation frames inside a `VP8X` WebP, which the canvas does not bound.
 *
 * @Codex, P1 on #184, and the THIRD container with this shape after the PNG
 * signature and the GIF frame rectangle: *"When an accepted animated WebP
 * declares a small `VP8X` canvas but contains an `ANMF` chunk whose frame
 * rectangle is much larger, this returns only the canvas dimensions and never
 * inspects the frame extents. The file therefore passes the 64-megapixel check
 * before `createImageBitmap()` may allocate from the oversized frame."*
 *
 * Every one of the three is the same sentence: **this function's job is to say
 * how much the decoder might allocate, and it was saying how big the author
 * says the picture is.** Any container with a frame layer over a canvas has the
 * hole, and fixing them one at a time is how the third one was still open.
 *
 * RIFF is a flat chunk list — four bytes of tag, four of little-endian length,
 * a payload padded to even — so this reads structure and never a pixel, and it
 * is bounded by the same step count as the GIF and JPEG walks. Exhausting that
 * bound is a refusal, for the reason the GIF walk was corrected an hour ago:
 * the frames it did not reach are exactly the ones it was asked about.
 *
 * `ANMF` payload: frame X and Y as 24-bit counts of TWO pixels, then width and
 * height as 24-bit minus one.
 */
function webpFrames(b: Uint8Array, canvas: PictureSize): PictureSize | undefined {
  let width = canvas.width;
  let height = canvas.height;
  let at = 12;
  let steps = HEADER_SCAN_STEPS;
  while (at + 8 <= b.length) {
    if (steps-- <= 0) return undefined;
    const size = le32(b, at + 4);
    if (ascii(b, at, 'ANMF') && at + 8 + 12 <= b.length) {
      const p = at + 8;
      width = Math.max(width, le24(b, p) * 2 + le24(b, p + 6) + 1);
      height = Math.max(height, le24(b, p + 3) * 2 + le24(b, p + 9) + 1);
    }
    const next = at + 8 + size + (size & 1);
    if (next <= at) return undefined;          // a zero or wrapped length
    at = next;
  }
  return { width, height };
}

function webp(b: Uint8Array): PictureSize | undefined {
  if (b.length < 30 || !ascii(b, 0, 'RIFF') || !ascii(b, 8, 'WEBP')) return undefined;
  if (ascii(b, 12, 'VP8X')) {
    // The canvas, and then every frame that claims to exceed it.
    return webpFrames(b, { width: le24(b, 24) + 1, height: le24(b, 27) + 1 });
  }
  if (ascii(b, 12, 'VP8L')) {
    // 14 bits of width then 14 of height, minus one each, packed little-endian
    // across four bytes after the 0x2f signature byte.
    const bits = b[21]! | (b[22]! << 8) | (b[23]! << 16) | (b[24]! << 24);
    return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
  }
  if (ascii(b, 12, 'VP8 ')) {
    // The keyframe header: a 3-byte start code at 23, then the dimensions in
    // 14 bits each with a 2-bit scale we discard — a decoder produces the
    // stated size, and the scale is a display hint.
    return { width: le16(b, 26) & 0x3fff, height: le16(b, 28) & 0x3fff };
  }
  return undefined;
}

/**
 * AVIF: the `ispe` box, found by scanning rather than by walking the box tree.
 *
 * Walking ISOBMFF properly means `meta` → `iprp` → `ipco` → `ispe`, with a
 * `iref`/`ipma` cross-reference to know which item the size belongs to. That is
 * a real parser, and for a bound it buys nothing: every `ispe` in the file
 * states a size the decoder may produce, so taking the LARGEST is the
 * conservative answer and the one a budget wants. Scanning is bounded by the
 * header region rather than by the file.
 *
 * ## Checked against the canvas-versus-frame pattern, and it does not have it
 *
 * Three containers here have had the same P1 — the PNG signature, the GIF frame
 * rectangle, the WebP `ANMF` extent — each one reporting how big the author
 * says the picture is rather than how much the decoder might allocate. AVIF is
 * the obvious fourth and a commit message speculated that it was. It is not:
 * taking the largest `ispe` anywhere in the file is already the maximum over
 * every declared extent, which is what the other three had to be changed to do,
 * and `budget.exhausted` already refuses rather than returning a partial best.
 *
 * Written down because the speculation is the sort of thing that gets repeated
 * and because the answer took one read: a note that it was checked is worth
 * more than the absence of a finding.
 */
function avif(b: Uint8Array): PictureSize | undefined {
  if (b.length < 16 || !ascii(b, 4, 'ftyp')) return undefined;
  /**
   * A truncated walk yields NOTHING, not the largest of what it happened to
   * see. @Codex, P1 on #184:
   *
   * > *"When an AVIF contains a small `ispe` before the 4,096-box limit and the
   * > primary image's larger `ispe` after it, exhausting this budget silently
   * > ends the generator while `avif()` still returns the earlier partial
   * > `best`. A crafted, highly compressed image can therefore be reported
   * > below `MAX_DECODED_PIXELS` and decoded at its much larger real
   * > dimensions, bypassing the allocation guard this parser exists to
   * > enforce."*
   *
   * The exact shape of the depth bound in `assertClosedVocabulary` two commits
   * ago, in a parser instead of a guard: a bound written to stop a hostile file
   * spinning the walk became the way past the check it protects. A cap that
   * makes the answer *smaller* is worse than no cap when the answer is a
   * budget, because under-reporting is the direction that lets bytes through.
   *
   * `undefined` here means "no dimensions", and `pictureSize`'s caller refuses
   * the preview for a file whose size it cannot establish — `0118`'s rule, and
   * the safe direction.
   */
  const budget = { left: MAX_BOXES, exhausted: false };
  let best: PictureSize | undefined;
  // Only inside the boxes that can CONTAIN a property. `mdat` is compressed
  // media and is never entered, which is the whole point — see the comment on
  // `boxes` below.
  for (const box of boxes(b, 0, b.length, 0, budget)) {
    if (box.type !== 'ispe' || box.end - box.start < 12) continue;
    // version(1) + flags(3), then width and height as big-endian 32-bit.
    const size = { width: be32(b, box.start + 4), height: be32(b, box.start + 8) };
    if (size.width > 0 && size.height > 0
      && (!best || size.width * size.height > best.width * best.height)) best = size;
  }
  return budget.exhausted ? undefined : best;
}

/** The containers whose payload is more boxes. Everything else — `mdat` above
 *  all — is opaque and must not be descended into. */
const CONTAINERS = new Set(['meta', 'iprp', 'ipco', 'iref', 'moov', 'trak', 'mdia', 'minf',
  'stbl', 'dinf', 'udta']);
/** `meta` is a FullBox: four bytes of version and flags before its children.
 *  Descending without skipping them lands one box header short and the walk
 *  reads garbage — which looks exactly like a file with no `ispe`. */
const FULL_BOX_CONTAINERS = new Set(['meta']);

interface IsoBox { readonly type: string; readonly start: number; readonly end: number }

/**
 * ISOBMFF boxes between `from` and `to`, depth-first, containers only.
 *
 * @Codex, P2 on #184: *"For an AVIF whose compressed `mdat` payload happens to
 * contain the four bytes `ispe`, this byte-by-byte scan treats the following
 * payload bytes as dimensions; if those integers exceed the pixel ceiling, a
 * valid image loses its preview despite its real `ispe` being within budget."*
 *
 * A regression I introduced one round earlier, widening the scan from 4 KiB to
 * the whole file to fix the opposite complaint — and the widening is exactly
 * what put arbitrary compressed bytes inside the search. Four bytes appear in
 * random data about once every four gigabytes, so a large enough AVIF finds one
 * eventually, and the consequence is asymmetric: a spurious `ispe` whose next
 * eight bytes read large wins the `best` comparison, and the file is refused.
 *
 * So: walk the structure. A box is a 32-bit length and a four-character type;
 * length 1 means a 64-bit length follows, and length 0 means "to the end of the
 * enclosing box". Descend only into the types that hold boxes, which is what
 * keeps `mdat` opaque. That is bounded by the file and reads only headers.
 */
/** A box walk is bounded by the file's own size — each box advances by at least
 *  eight bytes — but 64 MiB of eight-byte boxes is eight million yields on the
 *  main thread, and no real file has more than a few hundred. */
const MAX_BOXES = 4096;

/**
 * The budget, SHARED across the whole walk rather than per invocation.
 *
 * @Codex, P2 on #184: *"For a crafted AVIF that distributes boxes across many
 * nested container boxes, every recursive `boxes()` invocation resets `seen` to
 * zero, so the 4,096-box limit applies separately to each container rather than
 * to the complete parse. A file near the accepted 64 MiB ceiling can therefore
 * still make this synchronous ingestion path inspect millions of box headers
 * and freeze the page."*
 *
 * Right — a per-call counter bounds each container and bounds nothing, since
 * containers are what the file gets to choose how many of. A mutable cell
 * passed down is the smallest thing that makes the bound about the parse.
 */
function* boxes(
  b: Uint8Array, from: number, to: number, depth: number,
  budget: { left: number; exhausted: boolean } = { left: MAX_BOXES, exhausted: false },
): Generator<IsoBox> {
  /**
   * Depth-bounded, because a crafted file can nest containers as deeply as it
   * likes and a parser reading a hostile header is exactly where that matters.
   *
   * RECORDED, not just stopped — see `exhausted` below. Running out of depth is
   * the same kind of not-having-looked as running out of budget.
   */
  if (depth > 8) { budget.exhausted = true; return; }
  let at = from;
  while (at + 8 <= to) {
    budget.left -= 1;
    if (budget.left < 0) { budget.exhausted = true; return; }
    const declared = be32(b, at);
    const type = String.fromCharCode(b[at + 4]!, b[at + 5]!, b[at + 6]!, b[at + 7]!);
    let header = 8;
    let size = declared;
    if (declared === 1) {
      // 64-bit length. The high word is ignored: a box longer than 4 GiB cannot
      // be inside a file this door accepts, and reading it as a float would
      // lose precision rather than gain range.
      if (at + 16 > to) return;
      if (be32(b, at + 8) !== 0) return;
      size = be32(b, at + 12);
      header = 16;
    } else if (declared === 0) {
      size = to - at;                       // "to the end of the enclosing box"
    }
    if (size < header || at + size > to) return;   // malformed: stop, do not guess
    const start = at + header;
    const end = at + size;
    yield { type, start, end };
    if (CONTAINERS.has(type)) {
      yield* boxes(b, FULL_BOX_CONTAINERS.has(type) ? start + 4 : start, end,
        depth + 1, budget);
    }
    at = end;
  }
}

/**
 * The intrinsic size, or `undefined` when the header does not say.
 *
 * `undefined` is not "zero" and must not be treated as safe: it means the
 * budget cannot be applied, and the caller decides what to do about a file it
 * cannot measure. Returning a guess here would put the decision in the one
 * place that has the least information.
 */
/**
 * The mime whose SIGNATURE these bytes actually carry, or undefined.
 *
 * @Codex, P2 on `40b56c2`: `createImageBitmap` sniffs, so AVIF bytes offered
 * as JPEG decode as AVIF — while the derivative-index key carried the JPEG
 * probe, letting an AVIF decoder change reuse a stale derivative under an
 * unchanged key. Each parser validates its own signature and answers nothing
 * otherwise, so asking them in turn IS the sniff, with the same header-only
 * bounds every walk in this file carries.
 */
export function sniffedImageMime(bytes: Uint8Array): string | undefined {
  const parsers: readonly [string, (b: Uint8Array) => PictureSize | undefined][] = [
    ['image/png', png], ['image/jpeg', jpeg], ['image/gif', gif],
    ['image/webp', webp], ['image/avif', avif],
  ];
  for (const [mime, read] of parsers) if (read(bytes)) return mime;
  return undefined;
}

export function pictureSize(bytes: Uint8Array, mime: string): PictureSize | undefined {
  const declared = mime === 'image/png' ? png
    : mime === 'image/jpeg' ? jpeg
      : mime === 'image/gif' ? gif
        : mime === 'image/webp' ? webp
          : mime === 'image/avif' ? avif
            : undefined;
  /**
   * The DECLARED type first, then every other parser.
   *
   * @Codex, P1 on #184: *"When an accepted file's declared MIME differs from
   * its bytes — for example, a large PNG renamed as `.jpg` — `pictureSize()`
   * returns `undefined`, this condition skips the pixel ceiling, and
   * `createImageBitmap()` can still decode the actual image bytes."*
   *
   * Exactly the hole the caller's own comment admitted to and called
   * deliberate. It is not defensible once you notice that the browser does not
   * trust the declared type either: `createImageBitmap` sniffs, so a file whose
   * name lies decodes anyway and the budget was the only thing reading the
   * label. Each parser already validates its own signature and returns
   * `undefined` otherwise, so trying them all is a sniff with no new code.
   */
  const size = declared?.(bytes)
    /**
     * The declared parser EXCLUDED, because it has already run and returned
     * nothing. @Codex, P2 on #184: without this, a file whose declared type is
     * `image/jpeg` and whose bytes are not a parseable JPEG walks `jpeg()`
     * twice — *"roughly 128 million synchronous iterations on the main thread
     * before reporting the missing preview"*, on the one path whose whole
     * purpose is to keep the page answering.
     *
     * Each walk carries its own bound — `HEADER_SCAN_BYTES` for the JPEG byte
     * crawl, `HEADER_SCAN_STEPS` for the GIF and WebP chunk walks — and this
     * stops there being two of any of them.
     *
     * A walk that exhausts its bound returns `undefined`, so it falls through
     * to the parsers below it, every one of which refuses on a signature it
     * does not recognise. The overall answer is still a refusal, which is what
     * the ceiling needs.
     */
    ?? [png, jpeg, gif, webp, avif]
      .filter((read) => read !== declared)
      .reduce<PictureSize | undefined>((found, read) => found ?? read(bytes), undefined);
  // A header that parses to nothing is a header that did not parse. Zero and
  // NaN both reach `fitWithin` as a canvas of size zero, which throws several
  // steps later with a message about a canvas.
  return size && size.width > 0 && size.height > 0
    && Number.isFinite(size.width) && Number.isFinite(size.height)
    ? size : undefined;
}

/** A refusal that names the numbers, because "too big" leaves a person guessing
 *  whether to try a slightly smaller file or a very different one — the same
 *  argument `ingest.ts` makes about its byte ceiling. */
export class TooManyPixels extends Error {
  constructor(readonly size: PictureSize, readonly budget = MAX_DECODED_PIXELS) {
    super(
      `${size.width}x${size.height} is ${Math.round(size.width * size.height / 1e6)} `
      + `megapixels — the preview decoder stops at ${Math.round(budget / 1e6)}, `
      + 'because decoding one costs four bytes a pixel however small the file is',
    );
    this.name = 'TooManyPixels';
  }
}
